import Team from '@/components/Team/Team'
import Trapezoid from '@/components/Trapezoid/Trapezoid'
import { useTheme } from '@/contexts/ThemeContext'
import { useResolution } from '@/hooks/useResolution'
import { useTemplateAnimation } from '@/hooks/useTemplateAnimation'
import { getHomeCompetitor, getAwayCompetitor } from '@/services/jsonDataService'
import { hexToRgba } from '@/utils/colorUtils'

import { oversText, teamVisibility } from './cricketManualDisplay'

import './CricketManualBar.css'

import type { Competitor, CricketManualEventState, CricketManualScore, JsonMatchData } from '@/services/jsonDataService'
import type { MatchData } from '@/types'
import type { CSSProperties, FC } from 'react'

export interface CricketManualBarProps {
  matchData?: MatchData
  jsonData: JsonMatchData | null
  isVisible?: boolean
}

const MULTIPLIERS = {
  scoreFontSize: 1.3,
  oversFontSize: 0.65,
  battingDotSize: 0.28,
}

interface CricketManualBarSideProps {
  competitor?: Competitor
  isHome: boolean
  score?: CricketManualScore
  isBatting: boolean
  /** Pre-composed overs label; empty hides the overs element. */
  oversLabel: string
  /** The other team is hidden, so this side owns the whole bar. */
  isOnly: boolean
  baseHeight: string
}

const CricketManualBarSide: FC<CricketManualBarSideProps> = ({
  competitor,
  isHome,
  score,
  isBatting,
  oversLabel,
  isOnly,
  baseHeight,
}) => {
  // Sharing the bar, each half splits its width between name and score. Alone,
  // both shrink to content so the pair sits centred rather than pinned apart.
  const sideClass = isOnly ? 'cricket-bar-side-only' : `cricket-bar-side-${isHome ? 'home' : 'away'}`
  return (
    <div className={`cricket-bar-side ${sideClass}`}>
      <Team
        data={competitor}
        isHome={isHome}
        align={isOnly ? 'left' : isHome ? 'left' : 'right'}
        style={{ flex: isOnly ? '0 0 auto' : 1 }}
      />
      <div
        className="cricket-bar-score-stack"
        style={{ gap: `calc(${baseHeight} * 0.2)`, flex: isOnly ? '0 0 auto' : 2, justifyContent: 'center' }}
      >
        {isBatting && (
          <span
            className="cricket-bar-batting-dot"
            style={{
              width: `calc(${baseHeight} * ${MULTIPLIERS.battingDotSize})`,
              height: `calc(${baseHeight} * ${MULTIPLIERS.battingDotSize})`,
            }}
            aria-label="Batting"
          />
        )}
        <span className="cricket-bar-score" style={{ fontSize: `calc(${baseHeight} * ${MULTIPLIERS.scoreFontSize})` }}>
          {score?.score ?? ''}
        </span>
        {oversLabel !== '' && (
          <span
            className="cricket-bar-overs"
            style={{ fontSize: `calc(${baseHeight} * ${MULTIPLIERS.oversFontSize})` }}
          >
            {oversLabel}
          </span>
        )}
      </div>
    </div>
  )
}

const CricketManualBar: FC<CricketManualBarProps> = ({ jsonData, isVisible = true }) => {
  const theme = useTheme()
  const resolution = useResolution()
  const animationRef = useTemplateAnimation(isVisible, { animationType: 'slideUp', duration: 0.5 })

  const homeCompetitor = getHomeCompetitor(jsonData)
  const awayCompetitor = getAwayCompetitor(jsonData)

  const homeScore = homeCompetitor?.score as CricketManualScore | undefined
  const awayScore = awayCompetitor?.score as CricketManualScore | undefined

  const eventState = jsonData?.eventState as CricketManualEventState | undefined
  const batting = eventState?.batting ?? 1
  const teams = teamVisibility(eventState)
  const singleSide = !teams.home || !teams.away

  const cardStyle: CSSProperties = { width: resolution.height }
  const mainStyle: CSSProperties = {
    background: hexToRgba(theme.colors.primary, 0.99),
    minHeight: `calc(${theme.baseHeight} * 2.25)`,
  }

  return (
    <div ref={animationRef} className="cricket-bar-content-wrapper">
      <div className="cricket-bar-card" style={cardStyle} data-scorebar-panel>
        <Trapezoid
          style={{
            background: hexToRgba(theme.colors.primary, 0.95),
            width: Math.round(resolution.height * 0.3),
          }}
        >
          <img
            src="/assets/frogbox-logo-mono.png"
            alt="FrogBox"
            style={{ height: `calc(${theme.baseHeight} * 0.5)` }}
          />
        </Trapezoid>

        <div className="cricket-bar-main" style={mainStyle}>
          {teams.home && (
            <CricketManualBarSide
              competitor={homeCompetitor}
              isHome
              score={homeScore}
              isBatting={batting === 1}
              oversLabel={oversText(homeScore, eventState)}
              isOnly={singleSide}
              baseHeight={theme.baseHeight}
            />
          )}
          {teams.away && (
            <CricketManualBarSide
              competitor={awayCompetitor}
              isHome={false}
              score={awayScore}
              isBatting={batting === 2}
              oversLabel={oversText(awayScore, eventState)}
              isOnly={singleSide}
              baseHeight={theme.baseHeight}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default CricketManualBar
